import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../lib/theme';
import {
  List, MagnifyingGlass, Bell, Lifebuoy, Sun, Moon,
} from '@phosphor-icons/react';
import api from '../../lib/api';
import ProfileMenu from './ProfileMenu';
import HelpButton from '../training/HelpButton';
import { QUICK_BAR_HOME } from '../../lib/navigation';
import { hasViewAccess } from '../../lib/accessControl';

/**
 * Sticky premium top bar.
 * Left: ☰ + Cut2Pro logo (→ Home)
 * Right: Search · Raise Ticket · Alerts · Theme · Profile
 *
 * Compact, theme-adaptive, mobile-first.
 */
export default function TopBar({ onMenuClick, onSearchClick }) {
  const { user } = useAuth();
  const { effectiveMode, toggleMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);

  const isDark = effectiveMode === 'dark';

  // Load real counts. Polling every 60s keeps it light. Skip endpoints the
  // current user can't view to avoid noisy 403 console errors.
  const loadCounts = useCallback(async () => {
    if (!user) return;
    const canSeeAlerts  = hasViewAccess(user, '/alerts');
    const canSeeTickets = hasViewAccess(user, '/support');
    try {
      const promises = [];
      promises.push(canSeeAlerts ? api.get('/alerts') : Promise.resolve({ data: [] }));
      promises.push(canSeeTickets ? api.get('/support/tickets') : Promise.resolve({ data: [] }));
      const [a, t] = await Promise.allSettled(promises);
      const alerts = (a.value?.data || []).filter(x => !x.acknowledged && !x.resolved).length;
      const opens = (t.value?.data || []).filter(x => x.status === 'open' || x.status === 'in_progress').length;
      setAlertCount(alerts);
      setTicketCount(opens);
    } catch (e) { console.debug('[caught]', e); /* silent */ }
  }, [user]);
  useEffect(() => {
    if (!user) return;
    loadCounts();
    const id = setInterval(loadCounts, 60_000);
    return () => clearInterval(id);
  }, [user, loadCounts]);

  // Cmd/Ctrl+K hotkey for search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onSearchClick?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onSearchClick]);

  const goHome = () => {
    if (location.pathname === QUICK_BAR_HOME) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.dispatchEvent(new CustomEvent('home-refresh'));
    } else {
      navigate(QUICK_BAR_HOME);
    }
  };

  return (
    <header
      data-testid="top-bar"
      className="sticky top-0 z-30 bg-white/85 dark:bg-stone-900/85 backdrop-blur-xl border-b border-stone-100 dark:border-stone-800">
      <div className="flex items-center gap-2 px-3 sm:px-5 h-14">
        {/* Hamburger */}
        <button onClick={onMenuClick} aria-label="Open menu"
          data-testid="topbar-menu-btn"
          className="h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-700 dark:text-stone-200">
          <List size={18} weight="bold" />
        </button>

        {/* Logo + name → Home */}
        <button onClick={goHome} data-testid="topbar-logo"
          className="flex items-center gap-2 group">
          <img src="/logo-round.png" alt="Cut2Pro"
            className="h-10 w-10 rounded-full shadow-sm ring-1 ring-stone-200 dark:ring-stone-700 object-cover bg-white p-0.5" />
          <div className="text-left hidden sm:block">
            <p className="text-[15px] font-bold leading-none text-stone-800 dark:text-stone-100 group-hover:text-[#6B4D5F] tracking-tight">Cut2Pro</p>
            <p className="text-[9px] uppercase tracking-[0.18em] text-stone-400 leading-tight mt-0.5">v 1.0</p>
          </div>
        </button>

        <div className="flex-1" />

        {/* Right side icons */}
        <button onClick={onSearchClick} aria-label="Search"
          data-testid="topbar-search-btn"
          className="h-9 w-9 sm:w-auto sm:px-3 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center sm:justify-start gap-2 text-stone-500 dark:text-stone-400">
          <MagnifyingGlass size={16} weight="bold" />
          <kbd className="hidden md:inline-block text-[10px] px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-500">⌘K</kbd>
        </button>

        <HelpButton />

        <button onClick={() => navigate('/support')} aria-label="Raise ticket"
          data-testid="topbar-ticket-btn"
          className="relative h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-400">
          <Lifebuoy size={16} weight="bold" />
          {ticketCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#6B4D5F] text-white text-[9px] font-bold flex items-center justify-center">
              {ticketCount > 9 ? '9+' : ticketCount}
            </span>
          )}
        </button>

        <button onClick={() => navigate('/alerts')} aria-label="Alerts"
          data-testid="topbar-alerts-btn"
          className="relative h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-400">
          <Bell size={16} weight="bold" />
          {alertCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-stone-900 text-[9px] font-bold flex items-center justify-center"
              data-testid="alerts-count-badge">
              {alertCount > 9 ? '9+' : alertCount}
            </span>
          )}
        </button>

        <button onClick={toggleMode} aria-label="Toggle theme"
          data-testid="topbar-theme-btn"
          className="h-9 w-9 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center justify-center text-stone-500 dark:text-stone-400">
          {isDark ? <Sun size={16} weight="bold" /> : <Moon size={16} weight="bold" />}
        </button>

        <ProfileMenu />
      </div>
    </header>
  );
}
